import { useCallback, useEffect, useRef, useState } from "react";
import { getAllTablesByAreaId, getTableLayoutHierarchy, getTableStatusByCart } from "../../../config/Services";
import { assertKitchenResponse } from "./kitchenActions";
import { flattenTableCatalogue, enrichTableOccupancy } from "./tableWorkload";

export default function useTableCatalogue({ enabled, orgId, locId, layoutId }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const scopeRef = useRef("");
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Background polls keep the current tables on screen; only a new scope starts blank.
    const scope = JSON.stringify([orgId, locId, layoutId]);
    const initial = scopeRef.current !== scope;
    if (initial) {
      scopeRef.current = scope;
      setLoading(true);
      setTables([]);
      setError("");
    }
    const load = async () => {
      if (!layoutId) throw new Error("This location has no configured table layout.");
      const hierarchy = assertKitchenResponse(await getTableLayoutHierarchy(orgId, locId, layoutId, "nested")).data;
      if (!Array.isArray(hierarchy)) throw new Error("Unable to load the table catalogue.");
      const areas = hierarchy.flatMap((floor) => (floor.children || []).map((area) => ({ floor, area })));
      // One failing area (e.g. 404 for an empty area) must not hide the tables of the others.
      const results = await Promise.allSettled(areas.map(async ({ floor, area }) => {
        const res = assertKitchenResponse(await getAllTablesByAreaId({ parentId: area.mapId }));
        if (!Array.isArray(res.data)) throw new Error("Unable to load all tables.");
        return { floor, area, tables: res.data };
      }));
      const loaded = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
      if (areas.length && !loaded.length) throw results[0].reason;
      let catalogue = flattenTableCatalogue(loaded);
      const tableIds = catalogue.filter((table) => table.status === "OCCUPIED" && table.cartTableId)
        .map((table) => table.cartTableId);
      if (cancelled) return;
      if (initial) {
        setTables(catalogue.map((table) => ({
          ...table, occupancyLoading: tableIds.includes(table.cartTableId),
        })));
        setLoading(false);
      }
      if (tableIds.length) {
        // Cart status/time failure must not hide the otherwise valid Active kitchen view.
        try {
          const response = assertKitchenResponse(await getTableStatusByCart({
            sourceLocationId: locId, destinationLocationId: locId, tableIds,
          }));
          const statuses = response?.data?.data?.data;
          if (!Array.isArray(statuses)) throw new Error("Table occupancy timing is unavailable.");
          catalogue = enrichTableOccupancy(catalogue, statuses);
        } catch (_) {
          catalogue = enrichTableOccupancy(catalogue, []);
        }
      }
      if (!cancelled) setTables(catalogue);
    };
    load().catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, orgId, locId, layoutId, revision]);
  // Re-check table occupancy every 60 seconds.
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [enabled, refresh]);
  return { tables, loading, error, refresh };
}
